import { fileFetchUrl, storageUrl, generateGeminiTextApi } from '../services/api.js';
import { formatIDR } from './format.js';
import { formatMultipliedMoq } from './product.js';
import { checkRateLimitStatus, recordGeneration, formatCountdown } from './geminiRateLimiter.js';

const STOREFRONT_URL = (import.meta.env.VITE_STOREFRONT_URL ?? 'https://serojamakmur.shop').replace(/\/$/, '');

/**
 * Short link to the product's page on the website: /p/{item_id} redirects to
 * /produk/{item_id}-{slug}. Only published Catalogue New items have a page there.
 */
export function getProductShortUrl(item) {
  if (!item?.item_id || !item.is_published) return null;
  return `${STOREFRONT_URL}/p/${item.item_id}`;
}

/**
 * Generates formatted plain text for sharing a product (e.g. WhatsApp, Telegram, etc.)
 * Matches user-specified template:
 * *Product Name*
 * Rp {{Sell Price}}
 * Min. Order: {{MOQ}}
 * Lihat produk: {{short link}}   (published items only)
 */
export function getProductShareText(item) {
  if (!item) return '';
  const name = item.item_name || 'Produk';
  const price = item.item_price != null ? formatIDR(item.item_price) : 'Rp 0';
  const moq = formatMultipliedMoq(item) || '1';
  const url = getProductShortUrl(item);
  return `*${name}*\n${price}\nMin. Order: ${moq}${url ? `\nLihat produk: ${url}` : ''}`;
}

/**
 * Creates a composite image containing the product picture on top and
 * a clean white banner at the bottom with product name, price, and MOQ.
 * Returns a Promise<Blob> containing the PNG image.
 * @param {object} item
 * @param {number} [imageIndex=0] - Index of the image in item.pictures array to use
 */
export async function createProductCompositeImageBlob(item, imageIndex = 0) {
  // TODO: Support video thumbnails (extract poster frame) - currently only uses images
  const rawPath = item.pictures?.[imageIndex]?.pict_url || item.picture || null;
  const fetchUrl = rawPath ? (fileFetchUrl(rawPath) || storageUrl(rawPath)) : null;

  // 1. Attempt to load the product image
  let img = null;
  if (fetchUrl) {
    try {
      const res = await fetch(fetchUrl);
      if (res.ok) {
        const blob = await res.blob();
        const objUrl = URL.createObjectURL(blob);
        img = await new Promise((resolve) => {
          const image = new Image();
          image.onload = () => {
            URL.revokeObjectURL(objUrl);
            resolve(image);
          };
          image.onerror = () => {
            URL.revokeObjectURL(objUrl);
            resolve(null);
          };
          image.src = objUrl;
        });
      }
    } catch {
      // Fallback: direct Image loading
      img = await new Promise((resolve) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = () => resolve(null);
        image.src = fetchUrl;
      });
    }
  }

  // 2. Determine canvas geometry
  const targetWidth = img?.naturalWidth ? Math.max(800, Math.min(1200, img.naturalWidth)) : 800;
  const scale = targetWidth / 800;

  let imgHeight = targetWidth;
  if (img?.naturalWidth && img?.naturalHeight) {
    imgHeight = Math.round((targetWidth * img.naturalHeight) / img.naturalWidth);
  }

  // 3. Configure typography & layout
  const paddingX = Math.round(20 * scale);
  const paddingTop = Math.round(18 * scale);
  const paddingBottom = Math.round(20 * scale);
  const nameFontSize = Math.round(18 * scale);
  const priceFontSize = Math.round(16 * scale);
  const moqFontSize = Math.round(14 * scale);
  const lineSpacing = Math.round(6 * scale);

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Measure and wrap product name
  const maxTextWidth = targetWidth - paddingX * 2;
  ctx.font = `bold ${nameFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  const words = (item.item_name || 'Produk').split(' ');
  const nameLines = [];
  let currentLine = words[0] || '';

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const testLine = `${currentLine} ${word}`;
    if (ctx.measureText(testLine).width < maxTextWidth) {
      currentLine = testLine;
    } else {
      nameLines.push(currentLine);
      currentLine = word;
    }
  }
  if (currentLine) {
    nameLines.push(currentLine);
  }

  const displayLines = nameLines.slice(0, 3);
  if (nameLines.length > 3) {
    displayLines[2] = `${displayLines[2]}...`;
  }

  const nameBlockHeight = displayLines.length * (nameFontSize + lineSpacing);
  const priceBlockHeight = priceFontSize + lineSpacing;
  const moqBlockHeight = moqFontSize;
  const bannerHeight = paddingTop + nameBlockHeight + priceBlockHeight + moqBlockHeight + paddingBottom;

  canvas.width = targetWidth;
  canvas.height = imgHeight + bannerHeight;

  // 4. Render Background & Image
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  if (img) {
    ctx.drawImage(img, 0, 0, targetWidth, imgHeight);
  } else {
    // Elegant fallback placeholder
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(0, 0, targetWidth, imgHeight);
    ctx.fillStyle = '#94a3b8';
    ctx.font = `600 ${Math.round(22 * scale)}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Tidak ada foto produk', targetWidth / 2, imgHeight / 2);
  }

  // 5. Render White Banner
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, imgHeight, targetWidth, bannerHeight);

  // Subtle border separator between image and banner
  ctx.strokeStyle = '#f1f5f9';
  ctx.lineWidth = Math.max(1, Math.round(scale));
  ctx.beginPath();
  ctx.moveTo(0, imgHeight);
  ctx.lineTo(targetWidth, imgHeight);
  ctx.stroke();

  // 6. Render Text
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  let currentY = imgHeight + paddingTop;

  // Product Name (Bold, Dark)
  ctx.fillStyle = '#111827';
  ctx.font = `bold ${nameFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  for (const line of displayLines) {
    ctx.fillText(line, paddingX, currentY);
    currentY += nameFontSize + lineSpacing;
  }

  // Price (Bold, Dark)
  ctx.fillStyle = '#111827';
  ctx.font = `bold ${priceFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  const priceText = item.item_price != null ? formatIDR(item.item_price) : '—';
  ctx.fillText(priceText, paddingX, currentY);
  currentY += priceFontSize + lineSpacing;

  // Min. Order (Regular, Slate-600)
  ctx.fillStyle = '#4b5563';
  ctx.font = `500 ${moqFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`;
  const moqText = `Min. Order: ${formatMultipliedMoq(item) || '1'}`;
  ctx.fillText(moqText, paddingX, currentY);

  // 7. Output PNG Blob
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Canvas toBlob returned null'));
      }, 'image/png');
    } catch (err) {
      reject(err);
    }
  });
}

/**
 * Copies product share data to clipboard.
 * Supports:
 * - 'both': Writes composite image & plain text simultaneously to clipboard
 * - 'image': Writes only composite image
 * - 'text': Writes only plain text
 * @param {object} item
 * @param {'both'|'image'|'text'} mode
 * @param {number} [imageIndex=0] - Index of the image in item.pictures array to use
 */
export async function copyProductShareData(item, mode = 'both', imageIndex = 0) {
  const text = getProductShareText(item);

  if (mode === 'text') {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      throw new Error('Clipboard API tidak didukung browser ini');
    }
    return { success: true, mode: 'text' };
  }

  let imageBlob = null;
  try {
    imageBlob = await createProductCompositeImageBlob(item, imageIndex);
  } catch (imgErr) {
    console.warn('Gagal membuat gambar komposit:', imgErr);
  }

  if (mode === 'image') {
    if (!imageBlob) {
      throw new Error('Gagal memuat atau memproses gambar produk');
    }
    if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': imageBlob }),
      ]);
      return { success: true, mode: 'image' };
    }
    throw new Error('Browser tidak mendukung copy gambar langsung ke clipboard');
  }

  // mode === 'both'
  if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
    try {
      const data = {
        'text/plain': new Blob([text], { type: 'text/plain' }),
      };
      if (imageBlob) {
        data['image/png'] = imageBlob;
      }
      await navigator.clipboard.write([new ClipboardItem(data)]);
      return { success: true, mode: imageBlob ? 'both' : 'text-only' };
    } catch (clipErr) {
      console.warn('ClipboardItem multi-type write error, falling back to text:', clipErr);
      await navigator.clipboard.writeText(text);
      return { success: true, mode: 'text-fallback' };
    }
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return { success: true, mode: 'text' };
  }

  throw new Error('Clipboard tidak tersedia');
}

/**
 * Combines an AI-generated product description with the standardized product card text.
 * @param {string} description
 * @param {object} item
 * @returns {string}
 */
export function formatVariationShareText(description, item) {
  const baseText = getProductShareText(item);
  const cleanDesc = (description || '').trim();
  if (!cleanDesc) return baseText;
  return `${cleanDesc}\n\n${baseText}`;
}

/**
 * Calls Gemini API to generate 3 distinct, very short product copywriting drafts in Indonesian.
 * Returns an array of variation objects with { id, angle, description, full_text }.
 * @param {object} item
 * @param {object} [options]
 * @param {boolean} [options.noSugarGlaze=true] Whether to strictly prohibit marketing hyperbole and unverified claims
 * @param {string} [options.customFocus=''] Optional additional focus note from the seller
 * @returns {Promise<Array<object>>}
 */
export async function generateProductCompositeDrafts(item, options = {}) {
  if (!item) throw new Error('Data produk tidak tersedia');

  const { noSugarGlaze = true, customFocus = '' } = options;

  const name = item.item_name || 'Produk';
  const category = item.category_name || '-';
  const type = item.type_name || '-';
  const price = item.item_price != null ? formatIDR(item.item_price) : 'Rp 0';
  const moq = formatMultipliedMoq(item) || '1';

  const system_instruction = noSugarGlaze
    ? 'Kamu adalah copywriter B2B dan distributor grosir profesional berbahasa Indonesia. ' +
      'Tugasmu adalah membuat deskripsi penawaran produk untuk pembeli GROSIR (wholesaler, reseller, agen, pemilik toko retail), ' +
      'BUKAN pembeli eceran/end-user. ' +
      'DILARANG KERAS menggunakan kata-kata pemanis / hiperbola ("sugar glaze") seperti "kualitas terbaik", "luar biasa", "spektakuler", "tiada tanding", "cuan melimpah", "untung gila-gilaan". ' +
      'DILARANG KERAS membuat klaim yang belum tentu kebenarannya atau false urgency seperti "banyak dicari", "paling dicari", "stok cepat habis", "sebelum kehabisan", "kuota menipis", "terlaris", "pasti laku". ' +
      'Gunakan bahasa bisnis yang lugas, faktual, realistis, dan tenang: ketersediaan stok riil, spesifikasi teknis produk, harga grosir langsung, dan terms minimum order (MOQ) untuk toko. ' +
      'Tuliskan dalam 1-2 kalimat padat dan informatif (sekitar 15–25 kata). Format output HARUS berupa JSON array valid tanpa markdown atau teks pengantar.'
    : 'Kamu adalah copywriter B2B dan e-commerce grosir profesional berbahasa Indonesia. ' +
      'Tugasmu adalah membuat deskripsi penawaran produk untuk pembeli GROSIR (wholesaler, reseller, agen, pemilik toko retail), ' +
      'BUKAN pembeli eceran/end-user. Fokuskan pesan pada margin keuntungan jual kembali yang menarik, perputaran stok, ' +
      'pasokan partai besar, dan harga grosir distributor. Tuliskan dalam 1-2 kalimat padat dan persuasif ' +
      '(sekitar 15–25 kata). Format output HARUS berupa JSON array valid tanpa markdown atau teks pengantar.';

  const cleanCustomFocus = (customFocus || '').trim();
  const customFocusInstruction = cleanCustomFocus
    ? `\n- Catatan fokus khusus dari penjual: "${cleanCustomFocus}". Cantumkan poin ini secara ringkas dan wajar.`
    : '';

  const antiGlazeInstruction = noSugarGlaze
    ? '\n- ATURAN KETAT: DILARANG menggunakan pemanis/hiperbola ("terbaik", "luar biasa", "spektakuler") dan DILARANG membuat klaim yang belum tentu benar atau false urgency ("banyak dicari", "stok cepat habis", "sebelum kehabisan", "terlaris", "pasti laku"). Bahasa harus lugas, faktual, dan tenang.'
    : '';

  const prompt = `Buatkan tepat 3 variasi deskripsi produk untuk penawaran GROSIR / WHOLESALER / TOKO (B2B) dalam bahasa Indonesia untuk produk berikut:
- Nama Produk: ${name}
- Kategori: ${category}
- Tipe Produk: ${type}
- Harga Jual Grosir: ${price}
- Min. Order (MOQ): ${moq}${customFocusInstruction}${antiGlazeInstruction}

Gunakan tepat 3 nada bahasa (1 variasi per nada):
1. Promotif: ${noSugarGlaze ? 'Fokus faktual pada penawaran harga grosir langsung dan skema pembelian partai besar untuk stok toko.' : 'Fokus pada margin keuntungan dan penawaran harga grosir partai besar.'}
2. Kasual: ${noSugarGlaze ? 'Bahasa santai dan lugas antar partner bisnis, menginfokan kesiapan stok produk untuk melengkapi etalase toko.' : 'Santai dan akrab antar partner dagang, menekankan produk yang cocok untuk perputaran stok toko.'}
3. Profesional: ${noSugarGlaze ? 'Formal, jelas, dan kredibel, menekankan spesifikasi produk, keandalan pasokan, dan skema suplai B2B.' : 'Formal dan kredibel, menekankan konsistensi mutu dan kontinuitas pasokan untuk bisnis retail/grosir.'}

Panjang deskripsi: 1-2 kalimat padat dan ${noSugarGlaze ? 'faktual tanpa pemanis' : 'memikat'} (sekitar 15-25 kata), ditujukan khusus untuk pemilik toko/grosir.

Keluarkan HANYA JSON array dengan format persis:
[
  {
    "id": 1,
    "tone": "Promotif",
    "angle": "${noSugarGlaze ? 'Promotif (Harga Grosir)' : 'Promotif (Margin & Cuan)'}",
    "description": "${noSugarGlaze ? `Penawaran harga grosir langsung untuk ${name} dengan ketentuan order minimum ${moq}. Pasokan siap kirim partai besar untuk kebutuhan toko Anda.` : `Peluang margin laba maksimal untuk toko Anda dengan harga grosir spesial ${name}. Pasokan siap kirim partai besar untuk kebutuhan restock.`}"
  },
  {
    "id": 2,
    "tone": "Kasual",
    "angle": "Kasual (Partner Dagang)",
    "description": "${noSugarGlaze ? `Stok ${name} siap melengkapi etalase toko Anda. Skema harga grosir langsung dengan pemesanan minimal ${moq}.` : `Item siap masuk rak dagangan toko Anda. Yuk amankan stok ${name} selagi penawaran harga distributor terbaik.`}"
  },
  {
    "id": 3,
    "tone": "Profesional",
    "angle": "Profesional (B2B Supply)",
    "description": "${noSugarGlaze ? `Pasokan ${name} untuk kebutuhan ritel dan grosir. Ketersediaan stok terjaga dengan skema harga distributor dan ketentuan order yang jelas.` : `Mitra pasokan B2B terpercaya dengan jaminan mutu konsisten dan kapasitas suplai stabil untuk mendukung kelancaran bisnis retail Anda.`}"
  }
]`;

  let rawText = '';
  try {
    const rateStatus = checkRateLimitStatus(item.item_id);
    if (!rateStatus.allowed) {
      throw new Error(`Batas kuota generate untuk produk ini tercapai (maks. 2 generasi per 5 menit). Silakan coba lagi dalam ${formatCountdown(rateStatus.msRemaining)} atau beralih ke produk lain.`);
    }

    const res = await generateGeminiTextApi({ prompt, system_instruction, item_id: item.item_id });
    rawText = res?.text || '';
    if (rawText) {
      recordGeneration(item.item_id);
    }
  } catch (apiErr) {
    if (apiErr?.status === 429 || apiErr?.message?.includes('kuota')) {
      throw apiErr;
    }
    console.warn('Gagal menghubungi Gemini API, menggunakan draf cadangan:', apiErr);
  }

  let parsed = null;
  try {
    parsed = JSON.parse(rawText);
  } catch {
    const match = rawText.match(/\[[\s\S]*\]/);
    if (match) {
      try {
        parsed = JSON.parse(match[0]);
      } catch (jsonErr) {
        console.warn('Gagal parse JSON Gemini:', jsonErr);
      }
    }
  }

  // Fallback if parsing failed or array is empty
  if (!Array.isArray(parsed) || parsed.length === 0) {
    parsed = noSugarGlaze
      ? [
          {
            id: 1,
            tone: 'Promotif',
            angle: 'Promotif (Harga Grosir)',
            description: `Penawaran harga grosir langsung untuk ${name} dengan ketentuan minimum order ${moq}. Pasokan siap kirim partai besar untuk kebutuhan toko Anda.`,
          },
          {
            id: 2,
            tone: 'Kasual',
            angle: 'Kasual (Partner Dagang)',
            description: `Stok ${name} siap melengkapi etalase toko Anda. Skema harga grosir langsung dengan pemesanan minimal ${moq}.`,
          },
          {
            id: 3,
            tone: 'Profesional',
            angle: 'Profesional (B2B Supply)',
            description: `Pasokan ${name} untuk kebutuhan ritel dan grosir. Ketersediaan stok terjaga dengan skema harga distributor dan ketentuan order yang jelas.`,
          },
        ]
      : [
          {
            id: 1,
            tone: 'Promotif',
            angle: 'Promotif (Margin & Cuan)',
            description: `Peluang margin laba maksimal untuk toko Anda dengan harga grosir spesial ${name}. Pasokan siap kirim partai besar untuk kebutuhan restock.`,
          },
          {
            id: 2,
            tone: 'Kasual',
            angle: 'Kasual (Partner Dagang)',
            description: `Item siap masuk rak dagangan toko Anda. Yuk amankan stok ${name} selagi penawaran harga distributor terbaik.`,
          },
          {
            id: 3,
            tone: 'Profesional',
            angle: 'Profesional (B2B Supply)',
            description: `Mitra pasokan B2B terpercaya dengan jaminan mutu konsisten dan kapasitas suplai stabil untuk mendukung profitabilitas bisnis retail Anda.`,
          },
        ];
  }

  const defaultTones = ['Promotif', 'Kasual', 'Profesional'];
  return parsed.slice(0, 3).map((v, idx) => ({
    id: v.id || idx + 1,
    tone: v.tone || defaultTones[idx] || 'B2B',
    angle: v.angle || `${defaultTones[idx] || 'Variasi'} · B2B`,
    description: (v.description || '').trim(),
    full_text: formatVariationShareText(v.description, item),
  }));
}

/**
 * Calls Gemini API to regenerate 1 specific product copywriting draft for a given tone.
 * @param {object} item
 * @param {object} variation
 * @param {object} [options]
 * @param {boolean} [options.noSugarGlaze=true]
 * @param {string} [options.customFocus='']
 * @returns {Promise<object>} updated variation object
 */
export async function generateSingleProductCompositeVariation(item, variation, options = {}) {
  if (!item) throw new Error('Data produk tidak tersedia');
  if (!variation) throw new Error('Data variasi tidak tersedia');

  const rateStatus = checkRateLimitStatus(item.item_id);
  if (!rateStatus.allowed) {
    throw new Error(`Batas kuota generate untuk produk ini tercapai (maks. 2 generasi per 5 menit). Silakan coba lagi dalam ${formatCountdown(rateStatus.msRemaining)} atau beralih ke produk lain.`);
  }

  const { noSugarGlaze = true, customFocus = '' } = options;

  const name = item.item_name || 'Produk';
  const category = item.category_name || '-';
  const type = item.type_name || '-';
  const price = item.item_price != null ? formatIDR(item.item_price) : 'Rp 0';
  const moq = formatMultipliedMoq(item) || '1';
  const tone = variation.tone || 'Profesional';

  const system_instruction = noSugarGlaze
    ? 'Kamu adalah copywriter B2B dan distributor grosir profesional berbahasa Indonesia. ' +
      'Tugasmu adalah membuat deskripsi penawaran produk untuk pembeli GROSIR (wholesaler, reseller, agen, pemilik toko retail), ' +
      'BUKAN pembeli eceran/end-user. ' +
      'DILARANG KERAS menggunakan kata-kata pemanis / hiperbola ("sugar glaze") seperti "kualitas terbaik", "luar biasa", "spektakuler", "tiada tanding", "cuan melimpah", "untung gila-gilaan". ' +
      'DILARANG KERAS membuat klaim yang belum tentu kebenarannya atau false urgency seperti "banyak dicari", "paling dicari", "stok cepat habis", "sebelum kehabisan", "kuota menipis", "terlaris", "pasti laku". ' +
      'Gunakan bahasa bisnis yang lugas, faktual, realistis, dan tenang: ketersediaan stok riil, spesifikasi teknis produk, harga grosir langsung, dan terms minimum order (MOQ) untuk toko. ' +
      'Tuliskan dalam 1-2 kalimat padat dan informatif (sekitar 15–25 kata). Format output HARUS berupa JSON object valid tanpa markdown atau teks pengantar.'
    : 'Kamu adalah copywriter B2B dan e-commerce grosir profesional berbahasa Indonesia. ' +
      'Tugasmu adalah membuat deskripsi penawaran produk untuk pembeli GROSIR (wholesaler, reseller, agen, pemilik toko retail), ' +
      'BUKAN pembeli eceran/end-user. Fokuskan pesan pada margin keuntungan jual kembali yang menarik, perputaran stok, ' +
      'pasokan partai besar, dan harga grosir distributor. Tuliskan dalam 1-2 kalimat padat dan persuasif ' +
      '(sekitar 15–25 kata). Format output HARUS berupa JSON object valid tanpa markdown atau teks pengantar.';

  const cleanCustomFocus = (customFocus || '').trim();
  const customFocusInstruction = cleanCustomFocus
    ? `\n- Catatan fokus khusus dari penjual: "${cleanCustomFocus}". Cantumkan poin ini secara ringkas dan wajar.`
    : '';

  const antiGlazeInstruction = noSugarGlaze
    ? '\n- ATURAN KETAT: DILARANG menggunakan pemanis/hiperbola ("terbaik", "luar biasa", "spektakuler") dan DILARANG membuat klaim yang belum tentu benar atau false urgency ("banyak dicari", "stok cepat habis", "sebelum kehabisan", "terlaris", "pasti laku"). Bahasa harus lugas, faktual, dan tenang.'
    : '';

  let toneInstruction;
  if (tone.toLowerCase().includes('promotif')) {
    toneInstruction = noSugarGlaze
      ? 'Fokus faktual pada penawaran harga grosir langsung dan skema pembelian partai besar untuk stok toko.'
      : 'Fokus pada margin keuntungan dan penawaran harga grosir partai besar.';
  } else if (tone.toLowerCase().includes('kasual')) {
    toneInstruction = noSugarGlaze
      ? 'Bahasa santai dan lugas antar partner bisnis, menginfokan kesiapan stok produk untuk melengkapi etalase toko.'
      : 'Santai dan akrab antar partner dagang, menekankan produk yang cocok untuk perputaran stok toko.';
  } else {
    toneInstruction = noSugarGlaze
      ? 'Formal, jelas, dan kredibel, menekankan spesifikasi produk, keandalan pasokan, dan skema suplai B2B.'
      : 'Formal dan kredibel, menekankan konsistensi mutu dan kontinuitas pasokan untuk bisnis retail/grosir.';
  }

  const prompt = `Buatkan tepat 1 deskripsi produk untuk penawaran GROSIR / WHOLESALER / TOKO (B2B) dalam bahasa Indonesia dengan nada bahasa ${tone}:
- Nama Produk: ${name}
- Kategori: ${category}
- Tipe Produk: ${type}
- Harga Jual Grosir: ${price}
- Min. Order (MOQ): ${moq}
- Panduan Nada (${tone}): ${toneInstruction}${customFocusInstruction}${antiGlazeInstruction}

Panjang deskripsi: 1-2 kalimat padat dan ${noSugarGlaze ? 'faktual tanpa pemanis' : 'memikat'} (sekitar 15-25 kata), ditujukan khusus untuk pemilik toko/grosir.

Keluarkan HANYA JSON object dengan format:
{
  "tone": "${tone}",
  "description": "..."
}`;

  let rawText;
  try {
    const res = await generateGeminiTextApi({ prompt, system_instruction, item_id: item.item_id });
    rawText = res?.text || '';
    if (rawText) {
      recordGeneration(item.item_id);
    }
  } catch (apiErr) {
    console.warn('Gagal memanggil Gemini API untuk variasi tunggal:', apiErr);
    throw apiErr;
  }

  let parsed = null;
  try {
    parsed = JSON.parse(rawText);
  } catch {
    const match = rawText.match(/\{[\s\S]*\}/);
    if (match) {
      try {
        parsed = JSON.parse(match[0]);
      } catch (e) {
        console.warn('Gagal parse JSON Gemini object:', e);
      }
    }
  }

  const newDescription = (parsed?.description || '').trim();
  if (!newDescription) {
    throw new Error('Respons Gemini tidak memuat deskripsi yang valid');
  }

  return {
    ...variation,
    description: newDescription,
    full_text: formatVariationShareText(newDescription, item),
  };
}

/**
 * Copies a specific composite variation to clipboard (image + variation text).
 * @param {object} item
 * @param {object} variation
 * @param {'both'|'image'|'text'} mode
 * @param {number} [imageIndex=0] - Index of the image in item.pictures array to use
 */
export async function copyVariationShareData(item, variation, mode = 'both', imageIndex = 0) {
  // Rebuilt from the description rather than the saved full_text, so drafts saved
  // earlier still get the current price and the product link.
  const text = formatVariationShareText(variation?.description, item);

  if (mode === 'text') {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      throw new Error('Clipboard API tidak didukung browser ini');
    }
    return { success: true, mode: 'text' };
  }

  let imageBlob = null;
  try {
    imageBlob = await createProductCompositeImageBlob(item, imageIndex);
  } catch (imgErr) {
    console.warn('Gagal membuat gambar komposit:', imgErr);
  }

  if (mode === 'image') {
    if (!imageBlob) {
      throw new Error('Gagal memuat atau memproses gambar produk');
    }
    if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': imageBlob }),
      ]);
      return { success: true, mode: 'image' };
    }
    throw new Error('Browser tidak mendukung copy gambar langsung ke clipboard');
  }

  // mode === 'both'
  if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
    try {
      const data = {
        'text/plain': new Blob([text], { type: 'text/plain' }),
      };
      if (imageBlob) {
        data['image/png'] = imageBlob;
      }
      await navigator.clipboard.write([new ClipboardItem(data)]);
      return { success: true, mode: imageBlob ? 'both' : 'text-only' };
    } catch (clipErr) {
      console.warn('ClipboardItem multi-type write error, falling back to text:', clipErr);
      await navigator.clipboard.writeText(text);
      return { success: true, mode: 'text-fallback' };
    }
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return { success: true, mode: 'text' };
  }

  throw new Error('Clipboard tidak tersedia');
}

